import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

const VIEWPORT_W = 240;
const VIEWPORT_H = 320;
const MAX_OUTPUT_H = 1024;
const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.15;
const WHEEL_ZOOM_SPEED = 0.003;

const num = (val, min, max) => Math.min(max, Math.max(min, val));

// Per-axis clamping: if image is smaller than viewport → center; if larger → prevent gaps
const constrainOffset = (ox, oy, dw, dh) => ({
    x: dw <= VIEWPORT_W ? (VIEWPORT_W - dw) / 2 : num(ox, VIEWPORT_W - dw, 0),
    y: dh <= VIEWPORT_H ? (VIEWPORT_H - dh) / 2 : num(oy, VIEWPORT_H - dh, 0),
});

const PortraitCropper = ({ imageSrc, onCropComplete, onCancel }) => {
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const [naturalSize, setNaturalSize] = useState(null);

    // Refs for synchronous access in event handlers
    const zoomRef = useRef(MIN_ZOOM);
    const offsetRef = useRef({ x: 0, y: 0 });
    const baseScaleRef = useRef(1);
    const draggingRef = useRef(false);
    const dragStartRef = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

    // React state for rendering
    const [zoom, _setZoom] = useState(MIN_ZOOM);
    const [offset, _setOffset] = useState({ x: 0, y: 0 });

    const setZoom = (z) => { zoomRef.current = z; _setZoom(z); };
    const setOffset = (o) => { offsetRef.current = o; _setOffset(o); };

    // Height-fit: image height matches viewport height at zoom 1x
    const baseScale = naturalSize ? VIEWPORT_H / naturalSize.h : 1;
    baseScaleRef.current = baseScale;

    const scale = baseScale * zoom;
    const displayW = naturalSize ? naturalSize.w * scale : VIEWPORT_W;
    const displayH = naturalSize ? naturalSize.h * scale : VIEWPORT_H;

    const getDims = (z) => {
        if (!naturalSize) return { dw: VIEWPORT_W, dh: VIEWPORT_H };
        const s = baseScaleRef.current * z;
        return { dw: naturalSize.w * s, dh: naturalSize.h * s };
    };

    const applyZoom = useCallback((newZoom) => {
        const clamped = num(Math.round(newZoom * 100) / 100, MIN_ZOOM, MAX_ZOOM);
        const curZoom = zoomRef.current;
        const cur = offsetRef.current;
        const bs = baseScaleRef.current;
        const { dw, dh } = getDims(clamped);

        // Keep viewport center fixed during zoom
        const cx = VIEWPORT_W / 2;
        const cy = VIEWPORT_H / 2;
        const imgX = (cx - cur.x) / (bs * curZoom);
        const imgY = (cy - cur.y) / (bs * curZoom);
        const newOx = cx - imgX * bs * clamped;
        const newOy = cy - imgY * bs * clamped;

        setZoom(clamped);
        setOffset(constrainOffset(newOx, newOy, dw, dh));
    }, [naturalSize]);

    const handleImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.target;
        setNaturalSize({ w: naturalWidth, h: naturalHeight });
        // Height-fit: height = viewport, width = natural ratio
        const s = VIEWPORT_H / naturalHeight;
        const dw = naturalWidth * s;
        setZoom(MIN_ZOOM);
        setOffset({ x: (VIEWPORT_W - dw) / 2, y: 0 });
    };

    const handlePointerDown = (e) => {
        e.preventDefault();
        draggingRef.current = true;
        dragStartRef.current = { x: e.clientX, y: e.clientY, ox: offsetRef.current.x, oy: offsetRef.current.y };
        e.target.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e) => {
        if (!draggingRef.current) return;
        const dx = e.clientX - dragStartRef.current.x;
        const dy = e.clientY - dragStartRef.current.y;
        const { dw, dh } = getDims(zoomRef.current);
        setOffset(constrainOffset(dragStartRef.current.ox + dx, dragStartRef.current.oy + dy, dw, dh));
    };

    const handlePointerUp = () => {
        draggingRef.current = false;
    };

    // Non-passive wheel listener so preventDefault works
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onWheel = (e) => {
            e.preventDefault();
            applyZoom(zoomRef.current - e.deltaY * WHEEL_ZOOM_SPEED);
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [applyZoom]);

    const handleConfirm = () => {
        if (!naturalSize) return;
        const s = baseScaleRef.current * zoomRef.current;
        const cur = offsetRef.current;

        // Source rect in natural-image coordinates
        const srcX = -cur.x / s;
        const srcY = -cur.y / s;
        const srcW = VIEWPORT_W / s;
        const srcH = VIEWPORT_H / s;

        // Clip source rect to image bounds
        const clippedX = Math.max(0, srcX);
        const clippedY = Math.max(0, srcY);
        const clippedR = Math.min(naturalSize.w, srcX + srcW);
        const clippedB = Math.min(naturalSize.h, srcY + srcH);
        const clippedW = clippedR - clippedX;
        const clippedH = clippedB - clippedY;

        // Output sized proportionally, capped to MAX_OUTPUT_H
        const outputScale = Math.min(1, MAX_OUTPUT_H / clippedH);
        const outW = Math.round(clippedW * outputScale);
        const outH = Math.round(clippedH * outputScale);

        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imgRef.current, clippedX, clippedY, clippedW, clippedH, 0, 0, outW, outH);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        onCropComplete(dataUrl, outW, outH);
    };

    const zoomPercent = Math.round(((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100);

    return (
        <div className="flex flex-col items-center gap-4 py-2">
            <p className="text-sm text-muted-foreground">Drag to position &middot; scroll or slide to zoom</p>
            <div
                ref={containerRef}
                className="relative overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/30 bg-black cursor-grab active:cursor-grabbing"
                style={{ width: VIEWPORT_W, height: VIEWPORT_H, touchAction: 'none' }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    onLoad={handleImageLoad}
                    draggable={false}
                    className="absolute select-none max-w-none max-h-none"
                    style={{
                        width: displayW,
                        height: displayH,
                        left: offset.x,
                        top: offset.y,
                    }}
                />
            </div>
            {/* Zoom control */}
            <div className="flex items-center gap-3 w-[240px]">
                <button
                    className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30"
                    onClick={() => applyZoom(zoom - ZOOM_STEP)}
                    disabled={zoom <= MIN_ZOOM}
                    aria-label="Zoom out"
                >
                    <ZoomOut className="h-4 w-4" />
                </button>
                <Slider
                    value={[zoomPercent]}
                    min={0}
                    max={100}
                    step={1}
                    onValueChange={([v]) => applyZoom(MIN_ZOOM + (v / 100) * (MAX_ZOOM - MIN_ZOOM))}
                    className="flex-1"
                    aria-label="Zoom level"
                />
                <button
                    className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30"
                    onClick={() => applyZoom(zoom + ZOOM_STEP)}
                    disabled={zoom >= MAX_ZOOM}
                    aria-label="Zoom in"
                >
                    <ZoomIn className="h-4 w-4" />
                </button>
            </div>
            <div className="flex gap-3">
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button onClick={handleConfirm} disabled={!naturalSize}>Confirm Crop</Button>
            </div>
        </div>
    );
};

export default PortraitCropper;
